import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import React from 'react'
import { styled } from '@mui/material/styles'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import { Divider, Typography } from '@mui/material'
import moment from 'moment'
import './UserBillingGrid.css'
import { formatAmounts } from './../../utilities/generalUtils'

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        backgroundColor: 'var(--gp-card)',
        color: 'var(--gp-ink-mute)',
        fontFamily: 'var(--gp-font)',
        borderBottom: '1px solid var(--gp-hairline)',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        fontWeight: 400,
        color: 'var(--gp-ink)',
        fontFamily: 'var(--gp-font)',
        borderBottom: '1px solid var(--gp-hairline)',
    },
}))

const StyledTableRow = styled(TableRow)(() => ({
    '&:hover': {
        backgroundColor: 'var(--gp-surface)',
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}))

const moneySx = { fontFamily: 'var(--gp-mono)', textAlign: 'right' }

const TxChip = ({ type }) => {
    const isCredit = type === 'credit'
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 10px',
                borderRadius: 999,
                background: isCredit ? 'var(--gp-lime-soft)' : 'var(--gp-surface)',
                color: isCredit ? 'var(--gp-brand)' : 'var(--gp-ink-mute)',
                border: `1px solid ${isCredit ? 'transparent' : 'var(--gp-hairline)'}`,
                font: '500 11px/1 var(--gp-font)',
                letterSpacing: 0.3,
            }}
        >
            <span
                style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: isCredit ? 'var(--gp-brand)' : 'var(--gp-ink-mute)',
                }}
            />
            {type}
        </span>
    )
}

const UserBillingGrid = (props) => {
    const noTransactionHistory = () => {
        return (
            <div className="noInfoAvailableText">
                You have no transaction history
            </div>
        )
    }

    const billingTable = () => {
        return (
            <div className="tableContainer">
                <TableContainer>
                    <Table
                        sx={{
                            minWidth: 700,
                        }}
                        aria-label="customized table"
                        data-testid="cypress-billing-history-table"
                    >
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell>Transaction</StyledTableCell>
                                <StyledTableCell>Card Last 4</StyledTableCell>
                                <StyledTableCell sx={{ textAlign: 'right' }}>Credits</StyledTableCell>
                                <StyledTableCell sx={{ textAlign: 'right' }}>Debits</StyledTableCell>
                                <StyledTableCell sx={{ textAlign: 'right' }}>Refunded</StyledTableCell>
                                {!props.isRecurring && (
                                    <StyledTableCell sx={{ textAlign: 'right' }}>Balance</StyledTableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.userBillingItems.items.map((row) => (
                                <StyledTableRow key={row.uuid}>
                                    <StyledTableCell component="th" scope="row" sx={{ fontFamily: 'var(--gp-mono)' }}>
                                        {moment(row.createdAt).format(
                                            'M.DD.YY'
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <TxChip type={row.chargeType} />
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ fontFamily: 'var(--gp-mono)' }}>
                                        {row.paymentLast4 || '—'}
                                    </StyledTableCell>
                                    <StyledTableCell sx={moneySx}>
                                        {row.chargeType === 'credit'
                                            ? formatAmounts(row.amount)
                                            : '—'}
                                    </StyledTableCell>
                                    <StyledTableCell sx={moneySx}>
                                        {row.chargeType === 'debit'
                                            ? formatAmounts(row.amount)
                                            : '—'}
                                    </StyledTableCell>
                                    <StyledTableCell sx={moneySx}>
                                        {row.refunded
                                            ? formatAmounts(row.refunded)
                                            : '—'}
                                    </StyledTableCell>
                                    {!props.isRecurring && (
                                        <StyledTableCell sx={moneySx}>
                                            {formatAmounts(row.balance)}
                                        </StyledTableCell>
                                    )}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        )
    }

    const guestBillingTable = () => {
        const debitRows = props.userBillingItems.items.filter(
            (row) => row.chargeType === 'debit'
        )

        if (!debitRows.length) return noTransactionHistory()

        return (
            <div className="tableContainer">
                {debitRows.map((row, idx) => (
                    <React.Fragment key={row.uuid}>
                        <div style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <Typography variant="body1" sx={{ fontSize: '1.2rem' }} color="text.secondary">
                                    {moment(row.createdAt).format('MM/DD/YY')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: '1.2rem' }} color="text.secondary">
                                    CC Last 4: {row.paymentLast4}
                                </Typography>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" sx={{ fontSize: '1.3rem' }} color="text.primary">{formatAmounts(row.amount)}</Typography>
                                {!!row.refunded && (
                                    <Typography variant="body1" sx={{ fontSize: '1.3rem' }} color="text.primary">
                                        Refunded {formatAmounts(row.refunded)}
                                    </Typography>
                                )}
                            </div>
                        </div>
                        {idx < debitRows.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </div>
        )
    }

    if (props.isGuest) {
        return props.userBillingItems?.items?.length
            ? guestBillingTable()
            : noTransactionHistory()
    }

    return props.userBillingItems?.items?.length
        ? billingTable()
        : noTransactionHistory()
}

export default UserBillingGrid
